import config from '@/config';
import { useApp } from '@/contexts';

import { useState } from 'react';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { PageContainer, PageHeading } from '@/components/layout';
import { useGetEnvironmentsQuery } from '@/features/environments/api/environmentsApi';
import { useGetConfigTemplatesQuery } from '@/features/environments/api/environmentsApi';
import { useGetTenantQuery } from '@/features/tenants/api/tenantsApi';
import {
  useGetAppDeploymentsQuery,
  useGetTransformersQuery,
} from '@/features/transformers/api/transformersApi';
import type { DeploymentOption } from '@/types/schedule';
import { logger } from '@/utils/logger';

import { useCreateWebhookSubscriptionMutation, useGetAllObjectTypesQuery } from '../api/eventsApi';

const urlRegex = /[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/;
const fileRegex = /[-a-zA-Z0-9_.@()-]+\.[^.]+$/;

const getContrastColor = (bgColor: string): string => {
  if (!bgColor) return '#000';
  const color = bgColor.replace('#', '');
  const rgb = parseInt(color, 16);
  return rgb > 0xffffff / 2 ? '#000' : '#fff';
};

export const EventsSubscriptionFormPage = () => {
  const { appId, tenantId, app } = useApp();
  const navigate = useNavigate();

  // Step 1: Target selection
  const [targetType, setTargetType] = useState<'recipe' | 'wizard' | 'webhook'>('recipe');
  const [eventType, setEventType] = useState<'webhook' | 'File Upload Event'>('webhook');

  // Step 2: Object/Recipe selection
  const [objectType, setObjectType] = useState<string | null>(null);
  const [selectedDeployment, setSelectedDeployment] = useState<DeploymentOption | null>(null);
  const [filePattern, setFilePattern] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [sortByEnvironment, setSortByEnvironment] = useState(false);

  // Step indicator
  const [step, setStep] = useState<1 | 2>(1);

  // Fetch data
  const { data: tenant } = useGetTenantQuery(tenantId, { skip: !tenantId });
  const { data: environments } = useGetEnvironmentsQuery(
    { tenantId, appId: appId! },
    { skip: !tenantId || !appId }
  );
  const { data: transformers } = useGetTransformersQuery(appId!, { skip: !appId });
  const { data: deployments } = useGetAppDeploymentsQuery(
    { tenantId, appId: appId! },
    { skip: !tenantId || !appId }
  );
  const { data: objectTypes } = useGetAllObjectTypesQuery(
    { tenantKey: tenant?.key || '', appKey: app?.uniqueKey || '' },
    { skip: !tenant?.key || !app?.uniqueKey }
  );

  // Fetch config templates
  const { data: configTemplates } = useGetConfigTemplatesQuery();

  const [createSubscription, { isLoading: isCreating }] = useCreateWebhookSubscriptionMutation();

  // State for templates
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const availableTemplates = configTemplates?.map((t) => t.name).filter(Boolean) || [];

  // Categorize object types
  const systemObjects =
    objectTypes?.filter((o) => !o.private && !o.name.includes('transformer')) || [];
  const systemObjectsLegacy =
    objectTypes?.filter((o) => !o.private && o.name.includes('transformer')) || [];
  const restApiObjects = objectTypes?.filter((o) => o.private) || [];

  // Filter and sort deployments
  const sortedDeployments = deployments
    ? [...deployments].sort((a, b) => {
        if (sortByEnvironment) {
          return a.environmentId.localeCompare(b.environmentId);
        }
        return String(a.baseId).localeCompare(String(b.baseId));
      })
    : [];

  // Filter by recipe vs wizard
  const recipeDeployments = sortedDeployments.filter((d) => {
    const recipe = transformers?.find((t) => String(t.baseId) === d.baseId);
    return recipe && !recipe.generatedFromPi;
  });

  const wizardDeployments = sortedDeployments.filter((d) => {
    const recipe = transformers?.find((t) => String(t.baseId) === d.baseId);
    return recipe && recipe.generatedFromPi;
  });

  const displayedDeployments = targetType === 'wizard' ? wizardDeployments : recipeDeployments;

  // Validation
  const isUrlValid = urlRegex.test(webhookUrl);
  const isFilePatternValid = fileRegex.test(filePattern);

  const canProceedToStep2 =
    (targetType === 'recipe' || targetType === 'wizard') && selectedDeployment;

  const canCreateSubscription =
    objectType &&
    ((targetType === 'webhook' && isUrlValid) ||
      ((targetType === 'recipe' || targetType === 'wizard') &&
        selectedDeployment &&
        (eventType === 'webhook' || (eventType === 'File Upload Event' && isFilePatternValid))));

  const handleCreateSubscription = async () => {
    if (!tenant?.key || !app.uniqueKey || !objectType) return;

    try {
      if (eventType === 'File Upload Event') {
        // TODO: Implement file upload trigger creation
        // This requires saveTrigger API which is different from webhook subscriptions
        alert('File upload triggers not yet implemented');
        return;
      }

      // Create webhook subscription
      const verbFilter = '*';
      let deliveryUrl: string;

      if (targetType === 'webhook') {
        deliveryUrl = webhookUrl;
      } else {
        // Recipe or wizard subscription
        if (!selectedDeployment) {
          return; // Guard against null deployment
        }

        deliveryUrl = `${config.TRANS_API_URL}${tenantId}/${appId}/webhooks/environments/${selectedDeployment.environmentId}`;
      }

      const data = {
        objectTypeFilter: objectType,
        verbFilter,
        deliveryUrl,
      };

      if (targetType === 'webhook') {
        // URL webhook (no environment/recipe)
        await createSubscription({
          tenantId,
          appId: appId!,
          data,
        }).unwrap();
      } else {
        // Recipe webhook (with environment and recipe)
        if (!selectedDeployment) {
          return; // Guard against null deployment
        }
        await createSubscription({
          tenantId,
          appId: appId!,
          envId: selectedDeployment.environmentId,
          baseId: selectedDeployment.baseId,
          data,
        }).unwrap();
      }

      navigate(`/a/${appId}/events`);
    } catch (error) {
      logger.error('Failed to create subscription:', error);
      alert('Failed to create subscription. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate(`/a/${appId}/events`);
  };

  const handleTargetTypeChange = (
    _event: React.MouseEvent,
    value: 'recipe' | 'wizard' | 'webhook' | null
  ) => {
    if (value) {
      setTargetType(value);
      setSelectedDeployment(null);
      if (value === 'webhook') {
        setWebhookUrl('');
      }
    }
  };

  const handleDeploymentSelect = (deployment: DeploymentOption) => {
    if (
      selectedDeployment &&
      selectedDeployment.baseId === deployment.baseId &&
      selectedDeployment.environmentId === deployment.environmentId
    ) {
      setSelectedDeployment(null);
    } else {
      setSelectedDeployment(deployment);
    }
  };

  const handleObjectTypeChange = (objType: string) => {
    setObjectType(objType);
    if (objType === 'File Upload Event') {
      setEventType('File Upload Event');
      setTargetType('wizard');
      setWebhookUrl('');
    } else {
      setEventType('webhook');
    }
  };

  if (!app || !tenant) {
    return (
      <PageContainer>
        <CircularProgress />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title="New Subscription"
        subtitle="Create a webhook or file upload subscription for event notifications"
      />

      {step === 1 ? (
        <Box>
          {/* Target Type Selection */}
          <Box sx={{ mb: 3 }}>
            <ToggleButtonGroup
              value={targetType}
              exclusive
              onChange={handleTargetTypeChange}
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="recipe">Editor</ToggleButton>
              <ToggleButton value="wizard">Wizard</ToggleButton>
              <ToggleButton value="webhook" disabled={eventType === 'File Upload Event'}>
                Webhook
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Recipe/Wizard Deployments */}
          {(targetType === 'recipe' || targetType === 'wizard') && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Recipe Deployments ({displayedDeployments.length})
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSortByEnvironment(!sortByEnvironment)}
                >
                  Sort by: {sortByEnvironment ? 'Environment' : 'ID'}
                </Button>
              </Box>

              <Paper
                sx={{
                  height: 400,
                  overflowY: 'auto',
                  p: 1,
                  bgcolor: 'grey.50',
                }}
              >
                {displayedDeployments.length > 0 ? (
                  displayedDeployments.map((deployment) => {
                    const recipe = transformers?.find(
                      (t) => String(t.baseId) === deployment.baseId
                    );
                    const env = environments?.find(
                      (e) => String(e.id) === String(deployment.environmentId)
                    );

                    const isSelected =
                      selectedDeployment?.baseId === deployment.baseId &&
                      selectedDeployment?.environmentId === deployment.environmentId;

                    return (
                      <Box
                        key={`${deployment.baseId}-${deployment.environmentId}`}
                        onClick={() => handleDeploymentSelect(deployment)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          bgcolor: isSelected ? 'primary.light' : 'background.paper',
                          border: '1px solid',
                          borderColor: isSelected ? 'primary.main' : 'grey.300',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: isSelected ? 'primary.light' : 'grey.100',
                          },
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                            {recipe?.name}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '10px' }}>
                            ver. {deployment.version}
                          </Typography>
                          {env && (
                            <Chip
                              label={env.name}
                              size="small"
                              sx={{
                                height: '18px',
                                fontSize: '10px',
                                bgcolor: env.color || '#666',
                                color: getContrastColor(env.color || '#666'),
                                fontWeight: 'bold',
                              }}
                            />
                          )}
                          {recipe && (
                            <Chip
                              label={recipe.processingType}
                              size="small"
                              sx={{
                                height: '18px',
                                fontSize: '10px',
                                bgcolor: '#6D92B8',
                                color: '#ffffff',
                                fontWeight: 'bold',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ p: 2, color: 'text.secondary' }}>
                    No deployed recipes ({targetType === 'wizard' ? 'wizard' : 'editor'})
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {/* Webhook URL Input */}
          {targetType === 'webhook' && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Webhook URL
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Insert url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                error={webhookUrl !== '' && !isUrlValid}
                helperText={webhookUrl !== '' && !isUrlValid ? 'Invalid URL format' : ''}
                sx={{ maxWidth: 400 }}
              />
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button variant="outlined" color="error" size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!canProceedToStep2 && targetType !== 'webhook'}
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          {/* Step 2: Event Selection and Configuration */}
          <Box sx={{ width: 450, mb: 4, mt: 1 }}>
            <Box sx={{ borderTop: '1px solid #ddd', pt: 3, mb: 4 }}>
              {/* Selected Deployment */}
              <Typography sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>
                Selected Deployment
              </Typography>
              <Box sx={{ mb: 3 }}>
                {(() => {
                  if (targetType === 'webhook' && webhookUrl) {
                    return (
                      <Typography sx={{ fontSize: '11px', wordBreak: 'break-all' }}>
                        {webhookUrl}
                      </Typography>
                    );
                  }

                  if (selectedDeployment) {
                    return (
                      <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>
                        {selectedDeployment.transformerName ||
                          transformers?.find((t) => String(t.baseId) === selectedDeployment.baseId)
                            ?.name}
                      </Typography>
                    );
                  }

                  return null;
                })()}
              </Box>

              {/* Template Name - always visible */}
              <Typography sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>
                Template Name
              </Typography>
              <FormControl size="small" sx={{ mb: 3, minWidth: 200 }}>
                <Select
                  value={selectedTemplate || ''}
                  displayEmpty
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  <MenuItem value="">SELECT TEMPLATE</MenuItem>
                  {availableTemplates.length > 0 &&
                    availableTemplates
                      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                      .map((template) => (
                        <MenuItem key={template} value={template}>
                          {template.toUpperCase()}
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>

              {/* Event Selection */}
              <Typography sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>Event</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <Select
                  value={objectType || ''}
                  displayEmpty
                  onChange={(e) => handleObjectTypeChange(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    Select Object Type
                  </MenuItem>

                  {/* Event REST API Objects */}
                  <MenuItem disabled sx={{ fontWeight: 600, fontSize: '11px', opacity: 0.6 }}>
                    Event REST API
                  </MenuItem>
                  {restApiObjects.map((obj) => (
                    <MenuItem key={`rest-${obj.name}`} value={obj.name} sx={{ pl: 3 }}>
                      {obj.name}
                    </MenuItem>
                  ))}

                  {/* BrickFTP Objects */}
                  <MenuItem
                    disabled
                    sx={{ fontWeight: 600, fontSize: '11px', opacity: 0.6, mt: 1 }}
                  >
                    BrickFTP
                  </MenuItem>
                  <MenuItem value="File Upload Event" sx={{ pl: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileUploadIcon fontSize="small" />
                      File Upload Event
                    </Box>
                  </MenuItem>

                  {/* System Objects */}
                  <MenuItem
                    disabled
                    sx={{ fontWeight: 600, fontSize: '11px', opacity: 0.6, mt: 1 }}
                  >
                    System
                  </MenuItem>
                  {systemObjects.map((obj) => (
                    <MenuItem key={`sys-${obj.name}`} value={obj.name} sx={{ pl: 3 }}>
                      {obj.name}
                    </MenuItem>
                  ))}
                  {systemObjectsLegacy.map((obj) => (
                    <MenuItem key={`legacy-${obj.name}`} value={obj.name} sx={{ pl: 3 }}>
                      {obj.name} (legacy)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* File Pattern Input for File Upload Events */}
              {eventType === 'File Upload Event' && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '13px', mb: 1 }}>
                    Rules
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Type"
                    value="Filter by Value"
                    disabled
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="File Pattern"
                    placeholder="File Pattern"
                    value={filePattern}
                    onChange={(e) => setFilePattern(e.target.value)}
                    error={filePattern !== '' && !isFilePatternValid}
                    helperText={
                      filePattern !== '' && !isFilePatternValid ? 'Invalid file pattern' : ''
                    }
                  />
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
                  >
                    Directory: /{config.FTP_DIR || 'ftp/'}
                    {tenant?.key}/{app?.uniqueKey}/
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" color="error" size="small" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!canCreateSubscription}
              loading={isCreating}
              onClick={handleCreateSubscription}
            >
              Create Subscription
            </Button>
          </Box>
        </Box>
      )}
    </PageContainer>
  );
};
